import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
// components
import AppTemplate from '../../templates/appTemplate';
import SelfTest from '../../organisms/selfTest';
// actions
import dyopsAction from '../../../redux/actions/dyops';
import { applyInfantModeToDyops, isInfantDyopsSession, isInfantTestType } from '../../../tools/infantDefaults';
import { isOvalExamMode } from '../../../tools/resolveExamMode';
import { useInfantTargetJump } from '../../../hooks/useInfantTargetJump';
// types
import { appTheme, roomDataType, stateType } from '../../../types/types';
import { dyopsType } from '../../../types/dyops';
// mapping
import { mapping } from '../../../config/mapping';
// websockets
import { webSocketConnect } from '../../../websocket/websocket';
// localization
import strings from '../../../localization';
import appAction from '../../../redux/actions/app';
import { attachScreenWakeLock } from '../../../tools/screenWakeLock';

interface SelfTestPageProps {
  computedMatch: {
    params: { roomId: string; selfTestToken: string; isIntegration: string; theme: appTheme };
  };
}

const SelfTestPage: FC<SelfTestPageProps> = ({ computedMatch: { params } }: SelfTestPageProps) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isUserConnected, setIsUserConnected] = useState<boolean>(false);
  const [roomTestType, setRoomTestType] = useState<string | undefined>();

  const history = useHistory();
  const dispatch = useDispatch();
  const socket = useRef<any>();
  const dyopsRef = useRef<dyopsType | null>(null);
  const socketUrl = process.env.REACT_APP_API_SOCKET_URL || '';

  // store
  const { appLoading, dyops, appData } = useSelector((state: stateType) => ({
    appLoading: state.app.loading,
    dyops: state.dyops.dyops,
    appData: state.app.app,
  }));

  dyopsRef.current = dyops;

  useEffect(() => {
    const detachWakeLock = attachScreenWakeLock();
    return () => {
      detachWakeLock();
    };
  }, []);

  useEffect(() => {
    if (dyops.result?.testType) {
      setRoomTestType((prev) => prev || dyops.result.testType);
    }
  }, [dyops.result?.testType]);

  useEffect(() => {
    dispatch(appAction.editAppDataAction({ ...appData, theme: params.theme }, true));

    if (params.theme === 'dark') {
      const root = document.getElementById('root');
      root?.setAttribute('data-theme', 'dark');
    }

    socket.current = webSocketConnect(socketUrl, params.roomId, params.selfTestToken);

    socket.current.on('connect', () => {
      setIsConnected(true);

      dispatch(dyopsAction.getDyopsAction(socket.current.io.opts.query.roomId, dyops, startTest));
    });

    socket.current.on('onUpdatePatient', (data: { testType?: string; examMode?: dyopsType['examMode'] }) => {
      if (!data?.testType && !data?.examMode) {
        return;
      }

      if (data.testType) {
        setRoomTestType(data.testType);
      }

      if (dyopsRef.current && (data.examMode || data.testType)) {
        let next = dyopsRef.current;

        if (data.testType && isInfantTestType(data.testType)) {
          next = applyInfantModeToDyops(next, 'infant');
        }

        if (data.examMode) {
          next = {
            ...next,
            examMode: data.examMode,
            result: {
              ...next.result,
              examMode: data.examMode,
            },
          };
        }

        dispatch(dyopsAction.getDyops(next));
      }
    });

    socket.current.on('onUpdateDoctor', (data: { dyops: dyopsType }) => {
      const next = isInfantDyopsSession(data.dyops) ? applyInfantModeToDyops(data.dyops, 'infant') : data.dyops;
      dispatch(dyopsAction.getDyops(next));
    });

    socket.current.on('roomStatus', ({ room }: roomDataType) => {
      const patient = room.users.find((item) => item.type !== 'doctor');
      setIsUserConnected(patient && patient.connections > 1 ? true : false);
    });

    socket.current.on('disconnect', () => {
      dispatch(dyopsAction.clearDyops());
      setIsConnected(false);
    });

    const redirect = () => {
      history.push('/qr_scan');
    };

    !params && redirect();

    return () => {
      socket.current.disconnect();
    };
  }, []);

  const sessionTestType = roomTestType || dyops.result?.testType;
  const isInfantSession = isInfantDyopsSession(dyops, sessionTestType);
  const effectiveDyops = useMemo(
    () => (isInfantSession ? applyInfantModeToDyops(dyops, 'infant') : dyops),
    [dyops, isInfantSession],
  );

  useInfantTargetJump(
    effectiveDyops,
    isConnected && isUserConnected,
    isInfantSession ? socket.current : undefined,
    dispatch,
    sessionTestType,
  );

  const prepareDyopsForAction = (source: dyopsType): dyopsType =>
    isInfantSession ? applyInfantModeToDyops(source, 'infant') : source;

  const startTest = (source: dyopsType) => {
    const prepared = prepareDyopsForAction(source);
    const payload = mapping.buildDyopsData(prepared, 'start');
    socket.current.emit('emitUpdateDoctor', { dyops: payload });
    dispatch(dyopsAction.getDyops(payload));
  };

  const handleRestart = (source: dyopsType) => {
    const prepared = prepareDyopsForAction(source);
    const payload = mapping.buildDyopsData(prepared, 'selfRestart');
    socket.current.emit('emitUpdateDoctor', { dyops: payload });
    dispatch(dyopsAction.getDyops(payload));
  };

  const handleSubmit = () => {
    if (isInfantSession) {
      if (!dyops.isStarted) {
        startTest(dyops);
      }
      return;
    }

    (dyops.closeRightEye || dyops.closeLeftEye) && dyops.isStarted ? handleRestart(dyops) : startTest(dyops);
  };

  const handleCancel = () => {
    history.push({
      pathname: '/login',
    });
  };

  return (
    <AppTemplate
      submitLabel={
        isInfantSession
          ? strings.connectPage.labels.startTest
          : (dyops.closeRightEye || dyops.closeLeftEye) && dyops.isStarted
          ? strings.selfTest.control.restartTest
          : dyops.closeLeftEye
          ? strings.selfTest.testRight
          : strings.selfTest.testLeft
      }
      disabled={!isUserConnected}
      submitHidden={appLoading.status || !isConnected || (isInfantSession && dyops.isStarted)}
      heading=""
      description={
        appLoading.status || !isConnected || !dyops.isStarted
          ? ''
          : isInfantSession
          ? strings.infant.mobileControlDescription
          : isOvalExamMode(dyops)
          ? strings.selfTest.control.ovalDescription
          : strings.selfTest.control.description
      }
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      cancelHidden={params.theme === 'dark'}
      isConnectStatusShown={!appLoading.status && isConnected}
      isUserConnected={isUserConnected}
      customUserType="Second device"
      connectionLabelHidden={true}
      isDevice
      className={` small-content${dyops.isStarted ? ' content--self-test-device' : ''}`}
      isIntegration={params.isIntegration ? true : false}
    >
      {params && (
        <SelfTest
          socket={socket}
          appLoading={appLoading}
          theme={params.theme}
          dyops={dyops}
          sessionTestType={sessionTestType}
          screenRatio={appData.ratio}
          isConnected={isConnected}
          isIntegration={params.isIntegration}
        />
      )}
    </AppTemplate>
  );
};

export default SelfTestPage;
